import { c as _c } from "react/compiler-runtime";
import { alpha, Box, Tooltip, useTheme } from "@mui/material";
import React from "react";
import { selectNodeDataById, treeNodeSlice } from "#/entities/dataStructures/node/model/nodeSlice";
import type { CallFrame } from "#/features/callstack/model/callstackSlice";
import { safeStringify } from "#/shared/lib/stringifySolutionResult";
import { useAppDispatch, useAppSelector } from "#/store/hooks";
type CallstackNodeBadgeProps = {
  treeName: string;
  id: string;
  size?: number;
  showTooltip?: boolean;
};
export const CallstackNodeBadge: React.FC<CallstackNodeBadgeProps> = t0 => {
  const $ = _c(27);
  const {
    treeName,
    id,
    size: t1,
    showTooltip: t2
  } = t0;
  const size = t1 === undefined ? 32 : t1;
  const showTooltip = t2 === undefined ? true : t2;
  const theme = useTheme();
  let t3;
  if ($[0] !== id || $[1] !== treeName) {
    t3 = selectNodeDataById(treeName, id);
    $[0] = id;
    $[1] = treeName;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const nodeData = useAppSelector(t3);
  const dispatch = useAppDispatch();
  if (!nodeData) {
    let t4;
    if ($[3] !== id) {
      t4 = <span>{id}</span>;
      $[3] = id;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  let t4;
  if ($[5] !== dispatch || $[6] !== nodeData.id || $[7] !== treeName) {
    t4 = () => {
      dispatch(treeNodeSlice.actions.update({
        name: treeName,
        data: {
          id: nodeData.id,
          changes: {
            isHighlighted: true
          }
        }
      }));
    };
    $[5] = dispatch;
    $[6] = nodeData.id;
    $[7] = treeName;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleMouseEnter = t4;
  let t5;
  if ($[9] !== dispatch || $[10] !== nodeData.id || $[11] !== treeName) {
    t5 = () => {
      dispatch(treeNodeSlice.actions.update({
        name: treeName,
        data: {
          id: nodeData.id,
          changes: {
            isHighlighted: false
          }
        }
      }));
    };
    $[9] = dispatch;
    $[10] = nodeData.id;
    $[11] = treeName;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleMouseLeave = t5;
  let t6;
  if ($[13] !== theme.palette.primary.main) {
    t6 = alpha(theme.palette.primary.main, 0.1);
    $[13] = theme.palette.primary.main;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = size <= 24 ? "0.72rem" : "0.8rem";
  let t8;
  if ($[15] !== size || $[16] !== t6 || $[17] !== t7) {
    t8 = {
      width: size,
      height: size,
      display: "inline-grid",
      placeItems: "center",
      background: t6,
      borderRadius: "50%",
      fontSize: t7
    };
    $[15] = size;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== handleMouseEnter || $[20] !== handleMouseLeave || $[21] !== nodeData.value || $[22] !== t8) {
    t9 = <Box component="span" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} sx={t8}>{nodeData.value}</Box>;
    $[19] = handleMouseEnter;
    $[20] = handleMouseLeave;
    $[21] = nodeData.value;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const badge = t9;
  if (!showTooltip) {
    return badge;
  }
  const t10 = `id: ${id}`;
  let t11;
  if ($[24] !== badge || $[25] !== t10) {
    t11 = <Tooltip title={t10} arrow={true}>{badge}</Tooltip>;
    $[24] = badge;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
type CallstackArgumentsValueProps = {
  frame: CallFrame;
  showTooltip?: boolean;
};
export const CallstackArgumentsValue: React.FC<CallstackArgumentsValueProps> = t0 => {
  const $ = _c(13);
  const {
    frame,
    showTooltip: t1
  } = t0;
  const showTooltip = t1 === undefined ? true : t1;
  if (!("args" in frame)) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <span>---</span>;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  switch (frame.name) {
    case "setLeftChild":
    case "setRightChild":
      {
        let t2;
        if ($[1] !== frame.args.childId || $[2] !== frame.treeName || $[3] !== showTooltip) {
          t2 = frame.args.childId ? <CallstackNodeBadge treeName={frame.treeName} id={frame.args.childId} showTooltip={showTooltip} /> : <span>null</span>;
          $[1] = frame.args.childId;
          $[2] = frame.treeName;
          $[3] = showTooltip;
          $[4] = t2;
        } else {
          t2 = $[4];
        }
        return t2;
      }
    default:
      {
        let t2;
        if ($[5] !== frame.args) {
          t2 = safeStringify(frame.args);
          $[5] = frame.args;
          $[6] = t2;
        } else {
          t2 = $[6];
        }
        const value = t2;
        let t3;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = {
            overflow: "hidden",
            maxWidth: "70vh",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis"
          };
          $[7] = t3;
        } else {
          t3 = $[7];
        }
        let t4;
        if ($[8] !== showTooltip || $[9] !== value) {
          t4 = showTooltip ? <Tooltip title={<pre>{value}</pre>} arrow={true}><Box component="span">{value}</Box></Tooltip> : value;
          $[8] = showTooltip;
          $[9] = value;
          $[10] = t4;
        } else {
          t4 = $[10];
        }
        let t5;
        if ($[11] !== t4) {
          t5 = <Box sx={t3}>{t4}</Box>;
          $[11] = t4;
          $[12] = t5;
        } else {
          t5 = $[12];
        }
        return t5;
      }
  }
};